import { useEffect, useMemo, useState } from "react"

function formatUnitAgo(count: number, singular: string, plural: string): string {
  return count === 1 ? `1 ${singular} ago` : `${count} ${plural} ago`
}

function formatRelativeFromDiffMs(diffMs: number): string {
  if (diffMs < 60_000) {
    return "less than 1 min ago"
  }

  const minutes = Math.floor(diffMs / 60_000)
  if (minutes < 60) {
    return formatUnitAgo(minutes, "min", "mins")
  }

  const hours = Math.floor(diffMs / 3_600_000)
  if (hours < 24) {
    return formatUnitAgo(hours, "hr", "hrs")
  }

  const days = Math.floor(diffMs / 86_400_000)
  if (days < 7) {
    return formatUnitAgo(days, "day", "days")
  }
  if (days < 30) {
    const weeks = Math.floor(days / 7)
    return formatUnitAgo(weeks, "week", "weeks")
  }
  if (days < 365) {
    const months = Math.floor(days / 30)
    return months <= 1 ? "1 month ago" : `${months} months ago`
  }

  const years = Math.floor(days / 365)
  return formatUnitAgo(years, "year", "years")
}

/** `timestamp` is Unix seconds or ms (from API). */
export function formatRelativeTimeSyncAgo(
  timestamp: number,
  now = Date.now(),
): string {
  if (timestamp == null || Number.isNaN(timestamp) || timestamp === 0) {
    return "—"
  }
  const ms = timestamp < 1e12 ? timestamp * 1000 : timestamp
  const diffMs = Math.max(0, now - ms)
  return formatRelativeFromDiffMs(diffMs)
}

/**
 * Human-readable “time since” label that updates on an interval while `active`.
 * `timestamp` is Unix seconds or ms (see {@link formatRelativeTimeSyncAgo}).
 */
export function useRelativeTimestamp(
  timestamp: number | undefined,
  active = true,
  intervalMs = 30_000,
): string {
  const [tick, setTick] = useState(0)

  useEffect(() => {
    if (!active) {
      return
    }
    const id = globalThis.setInterval(() => {
      setTick((t) => t + 1)
    }, intervalMs)
    return () => globalThis.clearInterval(id)
  }, [active, intervalMs])

  return useMemo(
    () => formatRelativeTimeSyncAgo(timestamp ?? 0),
    [timestamp, tick],
  )
}
